/**
 * Exam API Service
 * Fetches exam definitions and skills from the backend API
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Default images for skills by category (fallback when API doesn't provide)
 */
const CATEGORY_IMAGES = {
  hygiene: 'https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?w=400&h=225&fit=crop&q=80',
  vital_signs: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=225&fit=crop&q=80',
  mobility: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=225&fit=crop&q=80',
  personal_care: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=225&fit=crop&q=80',
  elimination: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&h=225&fit=crop&q=80',
  safety: 'https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?w=400&h=225&fit=crop&q=80',
  nutrition: 'https://images.unsplash.com/photo-1505576399279-565b52d4ac71?w=400&h=225&fit=crop&q=80',
  positioning: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=225&fit=crop&q=80',
  default: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=225&fit=crop&q=80',
};

/**
 * Infer category from skill name/shortName
 */
function inferCategory(skill) {
  const name = (skill.name + ' ' + (skill.shortName || '')).toLowerCase();

  if (name.includes('handwashing') || name.includes('hand hygiene') || name.includes('mouth') ||
      name.includes('denture') || name.includes('bath') || name.includes('foot care') ||
      name.includes('peri')) {
    return 'hygiene';
  }
  if (name.includes('pulse') || name.includes('respiration') || name.includes('bp') ||
      name.includes('blood pressure') || name.includes('weight')) {
    return 'vital_signs';
  }
  if (name.includes('ambul') || name.includes('transfer') || name.includes('prom') ||
      name.includes('range of motion')) {
    return 'mobility';
  }
  if (name.includes('bedpan') || name.includes('urinary') || name.includes('catheter')) {
    return 'elimination';
  }
  if (name.includes('ppe') || name.includes('gown') || name.includes('glove')) {
    return 'safety';
  }
  if (name.includes('feed') || name.includes('nutrition')) {
    return 'nutrition';
  }
  if (name.includes('position') || name.includes('side')) {
    return 'positioning';
  }
  if (name.includes('stocking') || name.includes('dress')) {
    return 'personal_care';
  }

  return 'default';
}

/**
 * Transform backend skill format to frontend format
 */
function transformSkill(skill, index) {
  const category = skill.category || inferCategory(skill);

  // Extract simple ID: strip provider-state prefix (first 2 segments)
  // e.g., "credentia-ga-ambulation-gait-belt" -> "ambulation-gait-belt"
  const parts = skill.id.split('-');
  const simpleId = parts.length > 2 ? parts.slice(2).join('-') : skill.id;

  return {
    id: simpleId,
    skillNumber: skill.skillNumber || index + 1,
    name: skill.name,
    shortName: skill.shortName || skill.name.split('(')[0].trim(),
    description: skill.description || `Perform ${skill.shortName || skill.name} following proper technique`,
    totalSteps: skill.totalSteps || (skill.steps ? skill.steps.length : 0),
    criticalSteps: skill.criticalSteps || [],
    targetTimeSeconds: skill.targetTimeSeconds || 180,
    maxTimeSeconds: skill.maxTimeSeconds || 300,
    category: category,
    image: skill.imageUrl || skill.thumbnailUrl || skill.image || CATEGORY_IMAGES[category] || CATEGORY_IMAGES.default,
    isMandatory: simpleId === 'handwashing' || skill.isMandatory || false,
    demoVideoId: skill.demoVideoId,
    demoScript: skill.demoScript,
    steps: skill.steps || [],
    // Keep original data for reference
    _originalId: skill.id,
  };
}

/**
 * Fetch all available state exams
 */
export async function fetchStateExams() {
  const response = await fetch(`${API_BASE_URL}/api/exams/`);
  if (!response.ok) {
    throw new Error(`Failed to fetch exams: ${response.status}`);
  }
  return response.json();
}

/**
 * Fetch exams for a specific state
 */
export async function fetchExamsByState(stateCode) {
  const response = await fetch(`${API_BASE_URL}/api/exams/by-state/${stateCode}/`);
  if (!response.ok) {
    throw new Error(`Failed to fetch exams for state ${stateCode}: ${response.status}`);
  }
  return response.json();
}

/**
 * Fetch exam details by exam ID
 */
export async function fetchExam(examId) {
  const response = await fetch(`${API_BASE_URL}/api/exams/${examId}/`);
  if (!response.ok) {
    throw new Error(`Failed to fetch exam ${examId}: ${response.status}`);
  }
  return response.json();
}

/**
 * Fetch skills for an exam and transform to frontend format
 */
export async function fetchExamSkills(examId) {
  const response = await fetch(`${API_BASE_URL}/api/exams/${examId}/skills/`);
  if (!response.ok) {
    throw new Error(`Failed to fetch skills for exam ${examId}: ${response.status}`);
  }

  const data = await response.json();
  const skills = data.skills || [];

  // Transform to frontend format
  return skills.map((skill, index) => transformSkill(skill, index));
}

/**
 * Select random skills for exam (handwashing mandatory, avoid conflicting pairs)
 */
export function selectRandomSkills(allSkills, count = 5) {
  // Handwashing is always first
  const mandatory = allSkills.filter(s => s.isMandatory);
  const optional = allSkills.filter(s => !s.isMandatory);

  // Shuffle optional skills
  const shuffled = [...optional].sort(() => Math.random() - 0.5);

  // Select remaining skills (avoid conflicting combinations)
  const selected = [...mandatory];
  const excludePairs = [
    ['electronic-bp', 'manual-bp'],
    ['prom-knee-ankle', 'prom-shoulder'],
  ];

  for (const skill of shuffled) {
    if (selected.length >= count) break;

    // Check for excluded combinations
    let canAdd = true;
    for (const pair of excludePairs) {
      if (pair.includes(skill.id)) {
        const otherInPair = pair.find(id => id !== skill.id);
        if (selected.some(s => s.id === otherInPair)) {
          canAdd = false;
          break;
        }
      }
    }

    if (canAdd) {
      selected.push(skill);
    }
  }

  return selected;
}

/**
 * Select a single skill by id (practice mode). Falls back to first skill.
 */
export function selectSingleSkill(allSkills, skillId) {
  const match = allSkills.find(s => s.id === skillId);
  return match ? [match] : allSkills.slice(0, 1);
}

/**
 * Format seconds to MM:SS display
 */
export function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get skill by ID from a skills array
 */
export function getSkillById(skills, id) {
  return skills.find(skill => skill.id === id);
}

/**
 * Build API exam ID from provider and state code
 * Frontend uses "georgia", API uses "credentia-GA"
 */
export function buildApiExamId(provider, stateCode) {
  return `${provider}-${stateCode}`;
}

/**
 * Build API exam ID from exam registry entry
 */
export function getApiExamId(examRegistryEntry) {
  if (!examRegistryEntry) return null;
  return buildApiExamId(examRegistryEntry.provider, examRegistryEntry.stateCode);
}

// =============================================================================
// EXAM SUBMISSION API
// =============================================================================

const SCORING_API_URL = process.env.REACT_APP_SCORING_API_URL || `${API_BASE_URL}/api/scoring`;

/**
 * Get presigned URLs for uploading exam videos to S3
 * @param {string[]} skillIds - Array of skill IDs
 * @param {string} [examId] - Optional exam ID to reuse (for URL refresh)
 * @param {string} [stateCode] - Optional state code for descriptive filenames
 * @returns {Promise<{exam_id: string, uploads: Array<{skill_id, upload_url, public_url}>}>}
 */
export async function getUploadUrls(skillIds, examId, stateCode, contentType, examMode) {
  const { getAccessToken } = await import('./authApi');
  const token = getAccessToken();

  const body = { skills: skillIds };
  if (examId) body.exam_id = examId;
  if (stateCode) body.state_code = stateCode;
  if (contentType) body.content_type = contentType;
  if (examMode) body.exam_mode = examMode;

  const response = await fetch(`${SCORING_API_URL}/my-exams/upload-url/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Failed to get upload URLs: ${response.status}`);
  }

  return response.json();
}

/**
 * Upload a video blob to S3 using presigned URL with retry and progress tracking
 * @param {string} uploadUrl - Presigned S3 upload URL
 * @param {Blob} videoBlob - Video blob to upload
 * @param {Object} [options]
 * @param {function} [options.onProgress] - Progress callback (0-100)
 * @param {number} [options.maxRetries] - Max retry attempts (default 3)
 * @returns {Promise<void>}
 */
export async function uploadVideoToS3(uploadUrl, videoBlob, options = {}) {
  const { onProgress, maxRetries = 3, contentType = 'video/webm' } = options;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', contentType);

        if (onProgress) {
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              onProgress(Math.round((e.loaded / e.total) * 100));
            }
          };
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.ontimeout = () => reject(new Error('Upload timed out'));
        xhr.timeout = 300000; // 5 min timeout per upload

        xhr.send(videoBlob);
      });

      return; // Success — exit retry loop
    } catch (err) {
      const isLastAttempt = attempt === maxRetries;
      if (isLastAttempt) {
        throw err;
      }
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, attempt) * 1000;
      await new Promise(r => setTimeout(r, delay));
      if (onProgress) onProgress(0); // Reset progress for retry
    }
  }
}

/**
 * Submit exam for AI evaluation
 * @param {Object} examData - Exam submission data
 * @returns {Promise<{id: string, status: string}>}
 */
export async function submitExam(examData) {
  const { getAccessToken } = await import('./authApi');
  const token = getAccessToken();

  const response = await fetch(`${SCORING_API_URL}/my-exams/submit/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(examData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to submit exam: ${response.status}`);
  }

  return response.json();
}

/**
 * Check exam evaluation status
 * @param {string} examId - Exam ID
 * @returns {Promise<{id, status, total_score, passed, skills: Array}>}
 */
export async function checkExamStatus(examId) {
  const { getAccessToken } = await import('./authApi');
  const token = getAccessToken();

  const response = await fetch(`${SCORING_API_URL}/my-exams/${examId}/status/`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to check exam status: ${response.status}`);
  }

  return response.json();
}

/**
 * Poll for exam results until complete or timeout
 * @param {string} examId - Exam ID
 * @param {number} maxWaitMs - Maximum wait time in ms (default 5 minutes)
 * @param {number} pollIntervalMs - Poll interval in ms (default 5 seconds)
 * @returns {Promise<{id, status, total_score, passed, skills: Array}>}
 */
export async function waitForExamResults(examId, maxWaitMs = 300000, pollIntervalMs = 5000) {
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitMs) {
    const status = await checkExamStatus(examId);

    if (status.status === 'completed' || status.status === 'failed') {
      return status;
    }

    // Wait before polling again
    await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
  }

  throw new Error('Exam evaluation timed out');
}
